import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { Bank } from "../program/ProgramSlice";
import {
  CreditPrincipalWalletParams,
  CreditPrincipalWalletResponse,
  FetchPrincipalBankBalanceParams,
  FetchStoredBankStatementsParams,
  FetchPrincipalWalletTransactionParams,
  PrincipalBankBalanceResponse,
  PrincipalWalletBalanceResponse,
  StoredBankStatementsResponse,
  UploadBankStatementParams,
  UploadBankStatementResponse,
  WalletTransaction,
  WalletTransactionsResponse,
} from "./types";

export interface Principal {
  principalId: string;
  name: string;
  isDistributionOnly: boolean;
}

/* --------------------------------- Selp Recon Report ------------------------------ */

interface SelpReconReport {
  total_transactions: {
    label: string;
    value: number;
  } | null;
  verified_transaction: {
    label: string;
    value: number;
  } | null;
  missing_fincentive_transaction: {
    label: string;
    value: number;
  } | null;
  missing_selp_transaction: {
    label: string;
    value: number;
  } | null;
  amount_discrepancy_transaction: {
    label: string;
    value: number;
  } | null;
  date_discrepancy_transaction: {
    label: string;
    value: number;
  } | null;
  date_mismatch_transaction: {
    label: string;
    value: number;
  } | null;
  failed_transaction: {
    label: string;
    value: string | null;
  } | null;
}

export const fetchSelpReconReport = createAsyncThunk("recon/fetchSelpRecon", async ({ csvFile, fromDate, toDate }: { csvFile: File | null; fromDate: string; toDate: string }, { rejectWithValue }) => {
  const formData = new FormData();
  formData.append("fromDate", fromDate);
  formData.append("toDate", toDate);

  if (csvFile) {
    formData.append("csvFile", csvFile);
  }
  const token = localStorage.getItem("accessToken");

  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/utility/recon`,
      timeout: 100000,

      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      method: "POST",
    });

    return {
      total_transactions: {
        label: "Total Transactions",
        value: response.data["Total Transactions"],
      },
      verified_transaction: {
        label: "Transactions Verified",
        value: response.data["Transactions Verified"],
      },
      missing_fincentive_transaction: {
        label: "Transactions Missing in Fincentive",
        value: response.data["Transactions Missing in Fincentive"],
      },
      missing_selp_transaction: {
        label: "Transactions Missing in Selp",
        value: response.data["Transactions Missing in Selp"],
      },
      amount_discrepancy_transaction: {
        label: "Transactions with Amount Discrepancy",
        value: response.data["Transactions with Amount Discrepancy"],
      },
      date_discrepancy_transaction: {
        label: "Transactions with Date Discrepancy",
        value: response.data["Transactions with Date Discrepancy"],
      },
      date_mismatch_transaction: {
        label: "Transactions Skipped (Date Mismatch)",
        value: response.data["Transactions Skipped (Date Mismatch)"],
      },
      failed_transaction: {
        label: "Failed Transactions",
        value: response.data["Failed Transactions"],
      },
    } as SelpReconReport;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message ? error?.response?.data?.message : "Failed to Fetch Principals");
  }
});

/* --------------------------------- ICICI Recon Report ------------------------------ */

interface IciciReconReport {
  total_transactions: {
    label: string;
    value: number;
  } | null;
  verified_transaction: {
    label: string;
    value: number;
  } | null;
  missing_fincentive_transaction: {
    label: string;
    value: number;
  } | null;
  missing_icici_transaction_inactive: {
    label: string;
    value: number;
  } | null;
  missing_icici_transaction_failed: {
    label: string;
    value: number;
  } | null;
  amount_discrepancy_transaction: {
    label: string;
    value: number;
  } | null;
  date_discrepancy_transaction: {
    label: string;
    value: number;
  } | null;
  date_mismatch_transaction: {
    label: string;
    value: number;
  } | null;
  failed_transaction: {
    label: string;
    value: string | null;
  } | null;
}

export const fetchIciciReconReport = createAsyncThunk("recon/fetchIciciRecon", async ({ csvFile, date }: { csvFile: File | null; date: string }, { rejectWithValue }) => {
  const formData = new FormData();
  const [year, month, day] = date.split("-");
  formData.append("month", month);
  formData.append("year", year);
  if (csvFile) {
    formData.append("csvFile", csvFile);
  }
  const token = localStorage.getItem("accessToken");

  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/utility/recon/bank/icici`,
      timeout: 30000,

      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      method: "POST",
    });

    return {
      total_transactions: {
        label: "Total Transactions",
        value: response.data["Total Transactions"],
      },
      verified_transaction: {
        label: "Transactions Verified",
        value: response.data["Transactions Verified"],
      },
      missing_fincentive_transaction: {
        label: "Transactions Missing in Fincentive",
        value: response.data["Transactions Missing in Fincentive"],
      },
      missing_icici_transaction_failed: {
        label: "Transactions Missing in ICICI (Failures)",
        value: response.data["Transactions Missing in icici (Failures)"],
      },
      missing_icici_transaction_inactive: {
        label: "Transactions Missing in ICICI (Inactive)",
        value: response.data["Transactions Missing in icici (Inactive)"],
      },
      amount_discrepancy_transaction: {
        label: "Transactions with Amount Discrepancy",
        value: response.data["Transactions with Amount Discrepancy"],
      },
      date_discrepancy_transaction: {
        label: "Transactions with Date Discrepancy",
        value: response.data["Transactions with Date Discrepancy"],
      },
      date_mismatch_transaction: {
        label: "Transactions Skipped (Date Mismatch)",
        value: response.data["Transactions Skipped (Date Mismatch)"],
      },
      failed_transaction: {
        label: "Failed Transactions",
        value: response.data["Failed Transactions"],
      },
    } as IciciReconReport;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message ? error?.response?.data?.message : "Failed to Fetch Principals");
  }
});

/* --------------------------------- Principal List ------------------------------ */

export const fetchPrincipalList = createAsyncThunk("recon/fetchPrincipal", async (_, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");
  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/principals/`,
      timeout: 30000,

      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      method: "GET",
    });

    return response.data?.principals?.rows;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message ? error?.response?.data?.message : "Failed to Fetch Principals");
  }
});

export const createPrincipalWithFormData = createAsyncThunk(
  "principal/createPrincipalWithFormData",
  async (
    formData: FormData,
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/`,
        timeout: 30000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        method: "POST",
        data: formData,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to create Principal");
    }
  }
);

/* --------------------------------- Update Principal ------------------------------ */

export const updatePrincipalWithFormData = createAsyncThunk(
  "principal/updatePrincipalWithFormData",
  async (
    { principalId, formData }: { principalId: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}`,
        timeout: 30000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        method: "PATCH",
        data: formData,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to update Principal");
    }
  }
);

/* --------------------------------- Selected Principal ------------------------------------------- */

export const fetchSelectedPrincipal = createAsyncThunk("recon/fetchSelectedPrincipal", async (principalId:string, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");
  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}`,
      timeout: 30000,

      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      method: "GET",
    });

    return response.data?.principal;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message ? error?.response?.data?.message : "Failed to Fetch Principals");
  }
});


/* --------------------------------- Principal Wallet Balance ------------------------------ */

export const fetchPrincipalWalletBalance = createAsyncThunk<
  PrincipalWalletBalanceResponse,
  string,
  { rejectValue: string }
>(
  "principal/fetchPrincipalWalletBalance",
  async (principalId, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/wallet/balance`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch wallet balance"
      );
    }
  }
);

export const fetchPrincipalWalletTransaction = createAsyncThunk<
  WalletTransactionsResponse,
  FetchPrincipalWalletTransactionParams,
  { rejectValue: string }
>(
  "recon/fetchPrincipalWalletTransaction",
  async ({ principalId, bankId, limit, page, search, sortBy, sortOrder, transactionType, walletId, fromDate, toDate }, { rejectWithValue }) => {
    const token = localStorage.getItem('accessToken');

    try {
      let params: any = {};

      // Only add parameters if they are provided
      if (page) params["page"] = page;
      if (bankId) params["bankId"] = bankId;
      if (walletId) params["walletId"] = walletId;
      if (limit) params["limit"] = limit;
      if (sortBy) params["sortBy"] = sortBy;
      if (sortOrder) params["sortOrder"] = sortOrder;
      if (search) params["search"] = search;
      if (transactionType) params["transactionType"] = transactionType;
      if (fromDate) params["fromDate"] = fromDate;
      if (toDate) params["toDate"] = toDate;

      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/wallet/transactions`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        method: "GET",
        params
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message?.statusCode || "Failed to fetch principal wallet transaction"
      );
    }
  }
);


export const creditPrincipalWallet = createAsyncThunk<
  CreditPrincipalWalletResponse,
  CreditPrincipalWalletParams,
  { rejectValue: string }
>(
  "recon/creditPrincipalWallet",
  async ({ transactionNumber, amount, bankId, principalId, programId, remark}, { rejectWithValue }) => {
    const token = localStorage.getItem('accessToken');
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/wallet/credit`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        method: "POST",
        data: { transactionNumber, amount, bankId, principalId, programId, remark},
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message?.statusCode || "Failed to credit principal wallet"
      );
    }
  }
);

/* --------------------------------- Principal Banks ------------------------------ */

export const fetchPrincipalBankBalance = createAsyncThunk<
  PrincipalBankBalanceResponse,
  FetchPrincipalBankBalanceParams,
  { rejectValue: string }
>(
  "principal/fetchPrincipalBankBalance",
  async ({ principalId, principalBankId }, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/bank/balance`,
        timeout: 30000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        data: { principalBankId },
      });

      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch bank balance"
      );
    }
  }
);

export const fetchStoredBankStatements = createAsyncThunk<
  StoredBankStatementsResponse,
  FetchStoredBankStatementsParams,
  { rejectValue: string }
>(
  "principal/fetchStoredBankStatements",
  async ({ principalId, principalBankId, fromDate, toDate, limit }, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/bank/statements`,
        timeout: 60000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
        params: {
          principalBankId: principalBankId || undefined,
          fromDate,
          toDate,
          limit: limit ?? 500,
        },
      });

      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch bank statements"
      );
    }
  }
);

export const uploadPrincipalBankStatement = createAsyncThunk<
  UploadBankStatementResponse,
  UploadBankStatementParams,
  { rejectValue: string }
>(
  "principal/uploadBankStatement",
  async ({ principalId, principalBankId, file }, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("principalBankId", String(principalBankId));
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/bank/statement/upload`,
        timeout: 120000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        method: "POST",
        data: formData,
      });

      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to upload bank statement"
      );
    }
  }
);

export const fetchPrincipalBanks = createAsyncThunk(
  "recon/fetchPrincipalBanks",
  async (principalId: string, { rejectWithValue }) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/principals/${principalId}/banks`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      });

      // Extract banks from results array
      const banksData = response.data?.results || [];
      
      // Map the response to match the Bank interface
      // API returns principalBankId, we map it to bankId for consistency
      return banksData.map((bank: any) => ({
        bankId: bank.principalBankId,
        name: bank.name,
        logo: bank.logo || "",
        displayName: bank.name, // Use name as displayName since API doesn't provide displayName
      }));
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch principal banks"
      );
    }
  }
);





/* --------------------------------- Main State ------------------------------ */

interface PrincipalState {
  reconReport: SelpReconReport | null;
  iciciReconReport: IciciReconReport | null;
  principalList: Principal[] | null;
  loading: boolean;
  error: any;
  principalLoading: boolean;
  principalError: null | any;
  selectedPrincipal: Principal | null;
  selectedPrincipalLoading: boolean;
  selectedPrincipalError: null | any;
  principalWalletBalance: PrincipalWalletBalanceResponse | null;
  principalWalletBalanceLoading: boolean;
  principalWalletBalanceError: string | null;
  selectedPrincipalTransactions: WalletTransactionsResponse | null;
  selectedPrincipalTransactionsLoading: boolean;
  selectedPrincipalTransactionsError: null | any;
  principalBanks: Bank[] | null;
  principalBanksLoading: boolean;
  principalBanksError: null | any;
}

const initialState: PrincipalState = {
  reconReport: null,
  iciciReconReport: null,
  loading: false,
  error: null,
  principalList: null,
  principalLoading: false,
  principalError: null,
  selectedPrincipal: null,
  selectedPrincipalLoading: false,
  selectedPrincipalError: null,
  principalWalletBalance: null,
  principalWalletBalanceLoading: false,
  principalWalletBalanceError: null,
  selectedPrincipalTransactions: null,
  selectedPrincipalTransactionsLoading: false,
  selectedPrincipalTransactionsError: null,
  principalBanks: null,
  principalBanksLoading: false,
  principalBanksError: null
};

const principalSlice = createSlice({
  name: "principal",
  initialState,
  reducers: {
    clearReconReport(state) {
      state.reconReport = null;
      state.loading = false;
      state.error = null;
      state.iciciReconReport = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // Selp Recon Report
      .addCase(fetchSelpReconReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSelpReconReport.fulfilled, (state, action: PayloadAction<SelpReconReport>) => {
        state.loading = false;
        state.reconReport = action.payload;
      })
      .addCase(fetchSelpReconReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch Report";
      })

      // ICICI Recon Report
      .addCase(fetchIciciReconReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIciciReconReport.fulfilled, (state, action: PayloadAction<IciciReconReport>) => {
        state.loading = false;
        state.iciciReconReport = action.payload;
      })
      .addCase(fetchIciciReconReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch Report";
      })

      // Principal List
      .addCase(fetchPrincipalList.pending, (state) => {
        state.principalLoading = true;
        state.principalError = null;
      })
      .addCase(fetchPrincipalList.fulfilled, (state, action: PayloadAction<Principal[]>) => {
        state.principalLoading = false;
        state.principalList = action.payload;
      })
      .addCase(fetchPrincipalList.rejected, (state, action) => {
        state.principalLoading = false;
        state.principalError = action.payload;
      })

      // Selected Principal
      .addCase(fetchSelectedPrincipal.pending, (state) => {
        state.selectedPrincipalLoading = true;
        state.selectedPrincipalError = null;
      })
      .addCase(fetchSelectedPrincipal.fulfilled, (state, action: PayloadAction<Principal>) => {
        state.selectedPrincipalLoading = false;
        state.selectedPrincipal = action.payload;
      })
      .addCase(fetchSelectedPrincipal.rejected, (state, action) => {
        state.selectedPrincipalLoading = false;
        state.selectedPrincipalError = action.payload;
      })

      // Principal Wallet Balance
      .addCase(fetchPrincipalWalletBalance.pending, (state) => {
        state.principalWalletBalanceLoading = true;
        state.principalWalletBalanceError = null;
        state.principalWalletBalance = null;
      })
      .addCase(fetchPrincipalWalletBalance.fulfilled, (state, action: PayloadAction<PrincipalWalletBalanceResponse>) => {
        state.principalWalletBalanceLoading = false;
        state.principalWalletBalance = action.payload;
      })
      .addCase(fetchPrincipalWalletBalance.rejected, (state, action) => {
        state.principalWalletBalanceLoading = false;
        state.principalWalletBalanceError = action.payload as string;
      })

      // Principal wallet transactions
      .addCase(fetchPrincipalWalletTransaction.pending, (state) => {
        state.selectedPrincipalTransactionsLoading = true;
        state.selectedPrincipalTransactionsError = null;
      })
      .addCase(fetchPrincipalWalletTransaction.fulfilled, (state, action: PayloadAction<WalletTransactionsResponse>) => {
        state.selectedPrincipalTransactionsLoading = false;
        state.selectedPrincipalTransactions = action.payload;
      })
      .addCase(fetchPrincipalWalletTransaction.rejected, (state, action) => {
        state.selectedPrincipalTransactionsLoading = false;
        state.selectedPrincipalTransactionsError = action.payload;
      })

      .addCase(creditPrincipalWallet.pending, (state) => {
        state.selectedPrincipalLoading = true;
        state.selectedPrincipalError = null;
      })
      .addCase(creditPrincipalWallet.fulfilled, (state, action) => {
        state.selectedPrincipalLoading = false;
        state.selectedPrincipalError = null;
      })
      .addCase(creditPrincipalWallet.rejected, (state, action) => {
        state.selectedPrincipalLoading = false;
        state.selectedPrincipalError = action.payload;
      })

      // Principal Banks
      .addCase(fetchPrincipalBanks.pending, (state) => {
        state.principalBanksLoading = true;
        state.principalBanksError = null;
      })
      .addCase(fetchPrincipalBanks.fulfilled, (state, action: PayloadAction<Bank[]>) => {
        state.principalBanksLoading = false;
        state.principalBanks = action.payload;
      })
      .addCase(fetchPrincipalBanks.rejected, (state, action) => {
        state.principalBanksLoading = false;
        state.principalBanksError = action.payload;
      })
      .addCase(updatePrincipalWithFormData.pending, (state) => {
        state.principalLoading = true;
        state.principalError = null;
      })
      .addCase(updatePrincipalWithFormData.fulfilled, (state) => {
        state.principalLoading = false;
        state.principalError = null;
      })
      .addCase(updatePrincipalWithFormData.rejected, (state, action) => {
        state.principalLoading = false;
        state.principalError = action.payload;
      })
      .addCase(createPrincipalWithFormData.pending, (state) => {
        state.principalLoading = true;
        state.principalError = null;
      })
      .addCase(createPrincipalWithFormData.fulfilled, (state) => {
        state.principalLoading = false;
        state.principalError = null;
      })
      .addCase(createPrincipalWithFormData.rejected, (state, action) => {
        state.principalLoading = false;
        state.principalError = action.payload;
      })

  },
});

export const { clearReconReport } = principalSlice.actions;

// Export the reducer
export default principalSlice.reducer;
